import React, { useEffect, useState } from 'react';
import { addLead, patchLead } from '../services/LeadService';
import { toLeadPayload } from '../dto/LeadDTO';
import { useToast } from '../context/UIContext';
import { extractApiError } from '../utils/apiError';
import '../styles/ProjectAcquisition.css';

const EMPTY = {
    companyName: '', contactPerson: '', phone: '', email: '',
    city: '', industry: '', website: '', source: 'cold_call',
    priority: 'Medium', notes: '', nextFollowUpDate: '',
};

export default function LeadModal({ lead, sources = [], onClose, onSave }) {
    const toast = useToast();
    const [form, setForm] = useState(EMPTY);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setForm(lead
            ? {
                ...EMPTY,
                ...lead,
                contactPerson: lead.contactPerson ?? '',
                phone: lead.phone ?? '',
                email: lead.email ?? '',
                city: lead.city ?? '',
                industry: lead.industry ?? '',
                website: lead.website ?? '',
                source: lead.source ?? '',
                notes: lead.notes ?? '',
                nextFollowUpDate: lead.nextFollowUpDate?.substring(0, 10) ?? '',
            }
            : EMPTY);
        setError('');
    }, [lead]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        if (error) setError('');
    };

    const handleSubmit = () => {
        if (!form.companyName.trim()) { setError('Firma ist ein Pflichtfeld.'); return; }
        setSaving(true);
        const payload = { ...form, companyName: form.companyName.trim() };
        const request = lead
            ? patchLead(lead.id, toLeadPayload(payload))
            : addLead(payload);
        request
            .then(onSave)
            .catch(err => toast(extractApiError(err, 'Speichern fehlgeschlagen.')))
            .finally(() => setSaving(false));
    };

    const field = (name, label, type = 'text', extra = {}) => (
        <div className="form-field">
            <label className="form-label">{label}</label>
            <input
                type={type}
                name={name}
                value={form[name]}
                onChange={handleChange}
                className="input"
                placeholder={label}
                {...extra}
            />
        </div>
    );

    return (
        <div className="modal-overlay">
            <div className="modal modal-wide">
                <div className="modal-header">
                    <h3>{lead ? 'Lead bearbeiten' : 'Neuer Lead'}</h3>
                    <button className="modal-close-btn" onClick={onClose}>✕</button>
                </div>

                <div className="modal-body">
                    <div className="form-grid-2">
                        <div className="form-field">
                            <label className="form-label">
                                Firma<span className="form-required">*</span>
                            </label>
                            <input
                                type="text"
                                name="companyName"
                                value={form.companyName}
                                onChange={handleChange}
                                className={`input${error ? ' input-error' : ''}`}
                                placeholder="Firma"
                            />
                            {error && <span className="form-error-msg">{error}</span>}
                        </div>
                        {field('contactPerson', 'Ansprechpartner')}
                    </div>

                    <div className="form-section-label">Kontakt</div>
                    <div className="form-grid-3">
                        {field('phone', 'Telefon', 'tel')}
                        {field('email', 'E-Mail', 'email')}
                        {field('website', 'Website')}
                    </div>
                    <div className="form-grid-2">
                        {field('city', 'Ort')}
                        {field('industry', 'Branche')}
                    </div>

                    <div className="form-section-label">Akquise</div>
                    <div className="form-grid-3">
                        <div className="form-field">
                            <label className="form-label">Quelle</label>
                            <select name="source" value={form.source} onChange={handleChange} className="input">
                                <option value="">Nicht angegeben</option>
                                {sources.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                            </select>
                        </div>
                        <div className="form-field">
                            <label className="form-label">Priorität</label>
                            <select name="priority" value={form.priority} onChange={handleChange} className="input">
                                <option value="High">High</option>
                                <option value="Medium">Medium</option>
                                <option value="Low">Low</option>
                            </select>
                        </div>
                        {field('nextFollowUpDate', 'Anrufen am', 'date')}
                    </div>
                    {!lead && !form.nextFollowUpDate && (
                        <p className="acq-hint">Ohne Datum landet der Lead auf der heutigen Anrufliste.</p>
                    )}

                    <div className="form-field">
                        <label className="form-label">Notizen</label>
                        <textarea
                            name="notes"
                            value={form.notes}
                            onChange={handleChange}
                            className="input input-textarea"
                            rows={3}
                            placeholder="Warum ist die Firma interessant? Was ist der Aufhänger?"
                        />
                    </div>
                </div>

                <div className="modal-footer">
                    <button className="button" onClick={handleSubmit} disabled={saving}>
                        {saving ? 'Speichern…' : 'Speichern'}
                    </button>
                    <button className="button button-secondary" onClick={onClose}>Abbrechen</button>
                </div>
            </div>
        </div>
    );
}
